import { Attendance, AttendanceMonth, Giustificativo } from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { showTranslated, translate } from "@/config/translator";
import { KeyLabelPair, TempAttendance } from "@/config/types";
import { FocusEvent, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  addGiustificativoToEmployee,
  attendanceToTempAttendance,
  parseHours,
  POSITIVE_GIUSTIFICATIVI,
  removeGiustificativoFromEmployee,
  updateAttandenceAfterRegularHoursChange,
} from "../../../attendanceUtils";
import { clsx } from "clsx/lite";
import Fuse from "fuse.js";

type UseEmployeeAttendanceRowParams = {
  employeeAttendance: Attendance | TempAttendance;
  isLastRow: boolean;
  isExpanded: boolean;
  onSaveEdit: (isDraft: boolean, attendances?: AttendanceMonth) => void;
};

function highlightCell(
  employeeIndex: number,
  dayIndex: number,
  giustificativoIndex: number | null,
) {
  const element = document.getElementById(
    makeCellId(employeeIndex, dayIndex, giustificativoIndex),
  );
  element?.classList.add("bg-primary-extra-light!");
}

function cellBackToNormal(
  employeeIndex: number,
  dayIndex: number,
  giustificativoIndex: number | null,
) {
  const element = document.getElementById(
    makeCellId(employeeIndex, dayIndex, giustificativoIndex),
  );
  element?.classList.remove("bg-primary-extra-light!");
}

// cell id format: "attendance-row-employee-<idx>-day-<idx>-giustificativo-<giustificativo>"
// this is much faster than using react state

export function makeCellId(
  employeeIndex: number,
  dayIndex: number,
  giustificativoIndex: number | null,
) {
  // eslint-disable-next-line max-len
  const id = `attendance-row-employee-${employeeIndex}-day-${dayIndex}-giustificativo-${giustificativoIndex ?? ""}`;
  return id;
}

export function onMouseEnterCell(
  employeeIndex: number,
  dayIndex: number,
  giustificativoIndex: number | null,
  expandedEmployeeIndex: number | null,
  expandedGiustificativiCount: number | null,
) {
  for (let d = -2; d <= dayIndex; d++) {
    highlightCell(employeeIndex, d, giustificativoIndex);
  }
  for (let e = -1; e <= employeeIndex; e++) {
    highlightCell(e, dayIndex, null);
  }
  if (expandedEmployeeIndex === null || expandedGiustificativiCount === null)
    return;
  if (expandedEmployeeIndex === employeeIndex && giustificativoIndex !== null) {
    for (let g = 0; g <= giustificativoIndex; g++) {
      highlightCell(employeeIndex, dayIndex, g);
    }
  }

  if (expandedEmployeeIndex >= employeeIndex) return;
  for (let g = 0; g <= expandedGiustificativiCount; g++) {
    highlightCell(expandedEmployeeIndex, dayIndex, g);
  }
}

export function onMouseLeaveCell(
  employeeIndex: number,
  dayIndex: number,
  giustificativoIndex: number | null,
  expandedEmployeeIndex: number | null,
  expandedGiustificativiCount: number | null,
) {
  for (let d = -2; d <= dayIndex; d++) {
    cellBackToNormal(employeeIndex, d, giustificativoIndex);
  }
  for (let e = -1; e <= employeeIndex; e++) {
    cellBackToNormal(e, dayIndex, null);
  }
  if (expandedEmployeeIndex === employeeIndex && giustificativoIndex !== null) {
    for (let g = 0; g <= giustificativoIndex; g++) {
      cellBackToNormal(employeeIndex, dayIndex, g);
    }
  }

  if (expandedEmployeeIndex === null || expandedGiustificativiCount === null)
    return;
  if (expandedEmployeeIndex >= employeeIndex) return;
  for (let g = 0; g <= expandedGiustificativiCount; g++) {
    cellBackToNormal(expandedEmployeeIndex, dayIndex, g);
  }
}

export function useEmployeeAttendanceRow({
  employeeAttendance,
  isLastRow,
  isExpanded,
  onSaveEdit,
}: UseEmployeeAttendanceRowParams) {
  const { lang } = useContext(PspContext);
  const {
    setTempAttendanceMonth,
    attendanceMonth,
    setAttendanceMonth,
    isSubmitted,
    availableGiustificativi,
  } = useContext(AttendancesContext);

  const [currentEditingValue, setCurrentEditingValue] = useState<string | null>(
    null,
  );
  const [currentScratchEditingValue, setCurrentScratchEditingValue] = useState<
    string | null
  >(null);
  const [focusedDay, setFocusedDay] = useState<number | null>(null);

  const [giustificativoSearchQuery, setGiustificativoSearchQuery] =
    useState("");
  const [giustificativiOptions, setGiustificativiOptions] =
    useState<KeyLabelPair[]>();
  const [giustificativiSearchResults, setGiustificativiSearchResults] =
    useState<KeyLabelPair[]>();
  const [addGiustificativoPopoverOpen, setAddGiustificativoPopoverOpen] =
    useState(false);

  const [
    removeGiustificativoConfirmationOpen,
    setRemoveGiustificativoConfirmationOpen,
  ] = useState(false);
  const [wantsToRemoveGiustificativo, setWantsToRemoveGiustificativo] =
    useState<Giustificativo | null>(null);

  const giustificativiFuse = giustificativiOptions
    ? new Fuse(giustificativiOptions, { keys: ["label"] })
    : null;

  const alreadyAddedGiustificativi =
    employeeAttendance.days[0].giustificativi.map((g) => g.type);

  function onChangeGiustificativoSearchQuery(value: string) {
    if (!giustificativiFuse) return;

    setGiustificativoSearchQuery(value);
    setGiustificativiSearchResults(
      (value.length >= 1
        ? giustificativiFuse.search(value).map((result) => result.item)
        : giustificativiOptions
      )?.filter((g) => !alreadyAddedGiustificativi.includes(g.key)),
    );
  }

  function onAddGiustificativo(giustificativoShort: string | null) {
    if (giustificativoShort === null || !attendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      return;
    }

    const newGiustificativo = availableGiustificativi?.find(
      (g) => g.type === giustificativoShort,
    );
    if (!newGiustificativo) {
      toast(translate("giustificativoDoesNotExist", lang));
      return;
    }

    setAddGiustificativoPopoverOpen(false);
    setGiustificativoSearchQuery("");

    const newAttendance = addGiustificativoToEmployee(
      attendanceMonth,
      newGiustificativo,
      employeeAttendance.employee.matricola,
      employeeAttendance.employee.filiale,
    );
    setAttendanceMonth(newAttendance);
    setTempAttendanceMonth(attendanceToTempAttendance(newAttendance));
    onSaveEdit(true, newAttendance);
  }

  useEffect(() => {
    if (!availableGiustificativi) return;
    const giustificativiKeyLabels = availableGiustificativi.map((g) => ({
      key: g.type,
      label: `${g.type} - ${showTranslated(g.description, lang)}`,
    }));
    setGiustificativiOptions(giustificativiKeyLabels);
    setGiustificativiSearchResults(
      giustificativiKeyLabels.filter(
        (g) => !alreadyAddedGiustificativi.includes(g.key),
      ),
    );
  }, [alreadyAddedGiustificativi, availableGiustificativi, lang]);

  const giustificativi = employeeAttendance.days[0].giustificativi;

  // Bottom corners are rounded on the employee row itself unless
  // its giustificativi rows are expanded below it.
  const hasRoundedBottom =
    isLastRow && (!isExpanded || giustificativi.length === 0);

  function resetStateAfterRegularHoursUpdate() {
    setFocusedDay(null);
    setCurrentEditingValue(null);
    setCurrentScratchEditingValue(null);
  }

  function onBlurRegularHoursInput(
    event: FocusEvent<HTMLInputElement, Element>,
  ) {
    const newAttendance = updateAttandenceAfterRegularHoursChange(
      attendanceMonth,
      employeeAttendance,
      event.target.value,
      currentEditingValue,
      focusedDay,
      lang,
      resetStateAfterRegularHoursUpdate,
    );
    setAttendanceMonth(newAttendance);
    setTempAttendanceMonth(attendanceToTempAttendance(newAttendance));
    onSaveEdit(true, newAttendance);
  }

  function onBlurGiustificativoInput(
    event: FocusEvent<HTMLInputElement, Element>,
  ) {
    // make sure you actually changed something before saving
    const valueNumber = parseHours(event.target.value);
    if (
      currentEditingValue &&
      !Number.isNaN(valueNumber) &&
      valueNumber !== parseHours(currentEditingValue)
    ) {
      onSaveEdit(true);
    } else {
      if (!attendanceMonth) {
        toast(translate("somethingWentWrong", lang));
        setCurrentEditingValue(null);
        return;
      }
      setTempAttendanceMonth(attendanceToTempAttendance(attendanceMonth));
    }
    setCurrentEditingValue(null);
  }

  const daysChanged = employeeAttendance.days.map(
    (d) =>
      d.giustificativi.reduce((sum, g) => (sum += Number(g.hours)), 0) !== 0 ||
      Number(d.actualHours) !== Number(d.theoreticalHours),
  );

  const totalOreBancaOre = employeeAttendance.days.reduce<number>(
    (sum, day) =>
      sum +
      Number(
        day.giustificativi
          .filter((g) => POSITIVE_GIUSTIFICATIVI.includes(g.type))
          .reduce((sum, g) => sum + parseHours(g.hours), 0),
      ),
    0,
  );
  const totalHours =
    Math.round(
      employeeAttendance.days.reduce(
        (sum, day) => sum + Number(day.theoreticalHours),
        0,
      ) * 100,
    ) / 100;

  const totalActualHours =
    Math.round(
      employeeAttendance.days.reduce(
        (sum, day) =>
          sum +
          Number(day.actualHours) -
          day.giustificativi.reduce(
            (gSum, g) =>
              POSITIVE_GIUSTIFICATIVI.includes(g.type)
                ? gSum
                : gSum + parseHours(g.hours),
            0,
          ),
        0,
      ) * 100,
    ) / 100;

  const collapsibleCellClasses = clsx(
    "duration-200",
    "ease-in-out",
    "overflow-hidden",
    "px-2",
    "flex",
    "flex-row",
    "items-center",
    "text-font-secondary",
    "border-neutral",
    isExpanded && "border-b",
  );

  function openRemoveGiustificativoConfirmation(
    giustificativo: Giustificativo,
  ) {
    setWantsToRemoveGiustificativo(giustificativo);
    setRemoveGiustificativoConfirmationOpen(true);
  }

  function closeRemoveGiustificativoConfirmation() {
    setRemoveGiustificativoConfirmationOpen(false);

    setTimeout(() => {
      setWantsToRemoveGiustificativo(null);
    }, 250);
  }

  function onRemoveGiustificativo() {
    if (
      !wantsToRemoveGiustificativo ||
      !removeGiustificativoConfirmationOpen ||
      !attendanceMonth
    ) {
      toast(translate("somethingWentWrong", lang));
      return;
    }

    const newAttendanceMonth = removeGiustificativoFromEmployee(
      attendanceMonth,
      wantsToRemoveGiustificativo.type,
      employeeAttendance.employee.matricola,
      employeeAttendance.employee.filiale,
    );

    closeRemoveGiustificativoConfirmation();

    setAttendanceMonth(newAttendanceMonth);
    setTempAttendanceMonth(attendanceToTempAttendance(newAttendanceMonth));
    onSaveEdit(true, newAttendanceMonth);
  }

  return {
    daysChanged,
    hasRoundedBottom,
    totalOreBancaOre,
    totalHours,
    setCurrentEditingValue,
    onBlurGiustificativoInput,
    giustificativi,
    totalActualHours,
    setFocusedDay,
    onBlurRegularHoursInput,
    currentScratchEditingValue,
    setCurrentScratchEditingValue,
    focusedDay,
    isSubmitted,
    collapsibleCellClasses,
    onChangeGiustificativoSearchQuery,
    giustificativoSearchQuery,
    onAddGiustificativo,
    giustificativiSearchResults,
    addGiustificativoPopoverOpen,
    setAddGiustificativoPopoverOpen,
    openRemoveGiustificativoConfirmation,
    closeRemoveGiustificativoConfirmation,
    removeGiustificativoConfirmationOpen,
    wantsToRemoveGiustificativo,
    lang,
    onRemoveGiustificativo,
    attendanceMonth,
  };
}
